<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  currentInboxId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['close']);

const show = defineModel('show', { type: Boolean, default: false });
const store = useStore();
const { t } = useI18n();

const inboxes = useMapGetter('inboxes/getInboxes');
const selectedInbox = ref(null);
const isSubmitting = ref(false);

const inboxOptions = computed(() =>
  (inboxes.value || []).map(inbox => ({
    id: inbox.id,
    name: inbox.name,
  }))
);

const isSubmitDisabled = computed(() => {
  return (
    !selectedInbox.value ||
    Number(selectedInbox.value.id) === Number(props.currentInboxId) ||
    isSubmitting.value
  );
});

const closeModal = () => {
  show.value = false;
  emit('close');
};

const selectCurrentInbox = () => {
  selectedInbox.value =
    inboxOptions.value.find(
      inbox => Number(inbox.id) === Number(props.currentInboxId)
    ) || null;
};

const changeInbox = async () => {
  if (isSubmitDisabled.value) return;

  try {
    isSubmitting.value = true;
    await store.dispatch('changeInbox', {
      conversationId: props.conversationId,
      inboxId: selectedInbox.value.id,
    });
    useAlert(t('CONTACT_PANEL.CHANGE_INBOX.SUCCESS'));
    closeModal();
  } catch (error) {
    useAlert(t('CONTACT_PANEL.CHANGE_INBOX.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

watch(() => props.currentInboxId, selectCurrentInbox, { immediate: true });

watch(inboxOptions, selectCurrentInbox);

onMounted(() => {
  if (!inboxes.value?.length) {
    store.dispatch('inboxes/get');
  }
});
</script>

<template>
  <Modal v-model:show="show" :on-close="closeModal">
    <div class="flex flex-col w-full h-full">
      <div
        class="flex items-center justify-between px-8 py-4 border-b border-n-weak"
      >
        <h2 class="text-xl font-semibold text-n-slate-12">
          {{ $t('CONTACT_PANEL.CHANGE_INBOX.TITLE') }}
        </h2>
      </div>

      <div class="flex-1 px-8 py-6">
        <p class="mb-4 text-sm text-n-slate-11">
          {{ $t('CONTACT_PANEL.CHANGE_INBOX.DESCRIPTION') }}
        </p>

        <label class="block mb-2 text-sm font-medium text-n-slate-12">
          {{ $t('CONTACT_PANEL.CHANGE_INBOX.FIELD_LABEL') }}
        </label>
        <div class="multiselect-wrap--small">
          <multiselect
            v-model="selectedInbox"
            track-by="id"
            label="name"
            :placeholder="$t('CONTACT_PANEL.CHANGE_INBOX.PLACEHOLDER')"
            selected-label
            :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            deselect-label=""
            :max-height="160"
            :options="inboxOptions"
            :allow-empty="false"
            :option-height="104"
          >
            <template #noOptions>
              {{ $t('FORMS.MULTISELECT.NO_OPTIONS') }}
            </template>
          </multiselect>
        </div>
      </div>

      <div
        class="flex items-center justify-end gap-3 px-8 py-4 border-t border-n-weak"
      >
        <Button
          :label="$t('CONTACT_PANEL.CHANGE_INBOX.CANCEL')"
          color="slate"
          ghost
          @click="closeModal"
        />
        <Button
          :label="$t('CONTACT_PANEL.CHANGE_INBOX.SUBMIT')"
          color="primary"
          :disabled="isSubmitDisabled"
          :is-loading="isSubmitting"
          @click="changeInbox"
        />
      </div>
    </div>
  </Modal>
</template>
